import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, StickyNote } from 'lucide-react';
import { supabase } from '../../lib/supabase';

type RecordNote = {
  id: string;
  body: string;
  created_at: string;
};

type RecordNotesPanelProps = {
  recordType: 'transcript' | 'subscriber';
  recordId: string;
};

function formatWhen(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
}

/** Audit lines written when a receipt creates a chat or attaches a meeting. */
const RecordNotesPanel: React.FC<RecordNotesPanelProps> = ({ recordType, recordId }) => {
  const [notes, setNotes] = useState<RecordNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!recordId.trim()) {
      setNotes([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const { data, error: queryError } = await supabase
      .from('record_notes')
      .select('id, body, created_at')
      .eq('record_type', recordType)
      .eq('record_id', recordId)
      .order('created_at', { ascending: false });
    if (queryError) {
      setError(queryError.message);
      setNotes([]);
    } else {
      setNotes((data ?? []) as RecordNote[]);
    }
    setLoading(false);
  }, [recordId, recordType]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-8 text-sm text-gray-500 dark:text-gray-400">
        <Loader2 className="w-4 h-4 animate-spin" />
        Loading notes…
      </div>
    );
  }

  if (error) {
    return (
      <p className="text-sm text-red-600 dark:text-red-400">
        {error}
      </p>
    );
  }

  if (notes.length === 0) {
    return (
      <section className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-8 text-center">
        <StickyNote className="w-5 h-5 mx-auto text-gray-400" />
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
          Nothing recorded yet. A receipt writes a note when it creates a chat or attaches this record.
        </p>
      </section>
    );
  }

  return (
    <ol className="space-y-3">
      {notes.map((note) => (
        <li
          key={note.id}
          className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3"
        >
          <p className="text-sm text-gray-900 dark:text-gray-100">{note.body}</p>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{formatWhen(note.created_at)}</p>
        </li>
      ))}
    </ol>
  );
};

export default RecordNotesPanel;
